import { useState, useEffect, useRef } from "react";
import { api, errMsg, mediaUrl, uploadVideoChunked } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Copy, UploadCloud, Image as ImageIcon, Camera } from "lucide-react";

const fmtSize = (b) => (b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.round(b / 1024) + " KB");

export default function AdminMedia() {
  const [files, setFiles] = useState(null);
  const [uploads, setUploads] = useState({});
  const [qsel, setQsel] = useState({});
  const inputRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setFiles(r.data)).catch(() => setFiles([]));
  useEffect(() => { load(); }, []);

  const uploadFiles = async (list) => {
    for (const file of Array.from(list)) {
      const key = file.name + Date.now();
      setUploads((u) => ({ ...u, [key]: { name: file.name, pct: 0 } }));
      try {
        await uploadVideoChunked(file, (pct) => setUploads((u) => ({ ...u, [key]: { name: file.name, pct } })));
        toast.success(`${file.name} uploaded ✓`);
      } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
      setUploads((u) => { const n = { ...u }; delete n[key]; return n; });
    }
    load();
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("URL copied — paste it into any lecture's Video URL field"));
  };

  const transcode = async (id) => {
    try {
      await api.post(`/admin/media/${id}/transcode`);
      toast.success("Generating quality versions — this takes a few minutes");
      load();
      setTimeout(load, 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadThumb = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/media/${id}/thumbnail`, fd);
      toast.success("Thumbnail set ✓ — shown in previews and the player");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const captureFrame = (id) => {
    const video = document.querySelector(`video[data-video-id="${id}"]`);
    if (!video || !video.videoWidth || video.readyState < 2) {
      toast.error("Play the video and pause on the frame you want first");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) { toast.error("Could not capture frame"); return; }
      const fd = new FormData();
      fd.append("file", new File([blob], "frame.jpg", { type: "image/jpeg" }));
      try {
        await api.post(`/admin/media/${id}/thumbnail`, fd);
        toast.success("Frame saved as thumbnail ✓");
        load();
      } catch (err) { toast.error(errMsg(err)); }
    }, "image/jpeg", 0.92);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video from the library?")) return;
    try { await api.delete(`/admin/media/${id}`); toast.success("Video deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <h1 className="font-display font-bold text-xl mb-2" data-testid="admin-media-title">MEDIA LIBRARY</h1>
      <p className="text-sm mb-5" style={{ color: "var(--muted)" }}>Upload once, reuse everywhere — copy a video's URL into any lecture. Playback is enrollment-protected (previews stay public).</p>

      <label className="flex flex-col items-center justify-center gap-2 p-8 mb-6 rounded-xl cursor-pointer transition-colors"
        style={{ border: "2px dashed var(--line)", color: "var(--muted)" }} data-testid="media-upload-zone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files); }}>
        <UploadCloud size={28} style={{ color: "var(--gold)" }} />
        <span className="font-display text-sm" style={{ letterSpacing: 1 }}>DROP VIDEOS HERE OR CLICK TO UPLOAD</span>
        <span className="text-xs">mp4, webm, mov, mkv · up to 2GB each</span>
        <input ref={inputRef} type="file" multiple hidden accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
          data-testid="media-upload-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
      </label>

      {Object.entries(uploads).map(([k, u]) => (
        <div key={k} className="forge-card p-3 mb-2 text-sm" data-testid="media-upload-progress">
          <div className="flex justify-between mb-1"><span>{u.name}</span><span style={{ color: "var(--gold)" }}>{u.pct}%</span></div>
          <div style={{ height: 5, background: "var(--line)", borderRadius: 3 }}>
            <div style={{ height: "100%", width: `${u.pct}%`, background: "var(--gold)", borderRadius: 3, transition: "width .2s" }} />
          </div>
        </div>
      ))}

      {files === null ? <p style={{ color: "var(--muted)" }}>Loading…</p> : files.length === 0 ? (
        <div className="empty" data-testid="media-empty">No videos uploaded yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((f) => (
            <div key={f.id} className="forge-card overflow-hidden" data-testid={`media-item-${f.id}`}>
              <video key={f.id + (qsel[f.id] || "")} data-video-id={f.id} src={mediaUrl(f.url) + (qsel[f.id] ? `?q=${qsel[f.id]}` : "")}
                poster={f.thumb ? mediaUrl(`${f.url}/thumb`) + `?v=${encodeURIComponent(f.thumb.updated_at || "")}` : undefined}
                preload="metadata" controls className="w-full aspect-video bg-black" />
              <div className="p-3">
                <p className="text-sm font-medium truncate" title={f.original_filename}>{f.original_filename}</p>
                <p className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}</p>
                <p className="text-xs mt-1" style={{ color: "var(--bp)" }} data-testid={`media-qualities-${f.id}`}>
                  {f.transcode_status === "processing" ? "⏳ Generating quality versions…"
                    : f.renditions ? `Qualities: ${f.src_height ? f.src_height + "p" : "Original"}${Object.keys(f.renditions).sort((a, b) => b - a).map((h) => `, ${h}p`).join("")}`
                    : "Single quality (original only)"}
                </p>
                {f.usage.length > 0 ? (
                  <p className="text-xs mt-1" style={{ color: "var(--land)" }} data-testid={`media-usage-${f.id}`}>
                    Used in: {f.usage.map((u) => `${u.course_title} → ${u.lecture}`).join(" · ")}
                  </p>
                ) : (
                  <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Not used in any lecture yet</p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button className="btn-ghost !py-1 !px-3 text-xs flex items-center gap-1" data-testid={`media-copy-${f.id}`}
                    onClick={() => copyUrl(f.url)}><Copy size={12} /> Copy URL</button>
                  {f.renditions && Object.keys(f.renditions).length > 0 && (
                    <select className="input-dark !py-1 !px-2 text-xs !w-auto" data-testid={`media-quality-${f.id}`}
                      value={qsel[f.id] || ""} onChange={(e) => setQsel((s) => ({ ...s, [f.id]: e.target.value }))}>
                      <option value="">{f.src_height ? `${f.src_height}p (Original)` : "Original"}</option>
                      {Object.keys(f.renditions).sort((a, b) => b - a).map((h) => <option key={h} value={h}>{h}p</option>)}
                    </select>
                  )}
                  {!f.renditions && f.transcode_status !== "processing" && (
                    <button className="btn-ghost !py-1 !px-3 text-xs" data-testid={`media-transcode-${f.id}`}
                      onClick={() => transcode(f.id)}>⚙ Generate qualities</button>
                  )}
                  <label className="btn-ghost !py-1 !px-3 text-xs flex items-center gap-1 cursor-pointer" data-testid={`media-thumb-${f.id}`}>
                    <ImageIcon size={12} /> {f.thumb ? "Change thumbnail" : "Thumbnail"}
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadThumb(f.id, e)} />
                  </label>
                  <button className="btn-ghost !py-1 !px-3 text-xs flex items-center gap-1" data-testid={`media-frame-${f.id}`}
                    title="Pause the video on the frame you want, then click"
                    onClick={() => captureFrame(f.id)}><Camera size={12} /> Use current frame</button>
                  <button className="btn-ghost !py-1 !px-2 text-xs ml-auto hover:!text-red-400" data-testid={`media-delete-${f.id}`}
                    onClick={() => remove(f.id)}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
